/**
 * The merge log: every merge the dedup pass or a person made, and its undo.
 *
 * Owner, 25 Sep: de-duplication runs by itself, so every merge it makes must
 * be on a log a person can read — what was folded into what, why, by whom —
 * and must come apart in one click. Undo puts back exactly what the merge
 * changed (a value corrected since is kept, and said so) and remembers the
 * pair as two, so the next pass never folds it again.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, RotateCcw } from 'lucide-react'

import { crmErrorDetail, crmService } from '@/services/crm'
import { formatStamp, relativeTime } from './crmFields'

const BUTTON =
  'inline-flex items-center gap-1 rounded-md border border-amber-400/15 px-2 py-0.5 text-[10px] text-stone-300 hover:border-amber-400/30 hover:text-stone-100 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60 disabled:cursor-not-allowed disabled:opacity-50'

export default function MergeLog({ onOpenContact }: { onOpenContact?: (id: string) => void }) {
  const queryClient = useQueryClient()
  const [note, setNote] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null)
  const log = useQuery({ queryKey: ['crm', 'merges'], queryFn: () => crmService.getMerges(50) })

  const undo = useMutation({
    mutationFn: (id: string) => crmService.undoMerge(id),
    onSuccess: (result) => {
      setNote({
        tone: 'ok',
        text: result.kept.length
          ? `Merge undone. Kept ${result.kept.join(', ')} as corrected since the merge.`
          : 'Merge undone. Both records are back as they were.',
      })
      for (const key of [['crm', 'merges'], ['crm', 'contacts'], ['crm', 'activity'], ['crm', 'duplicates'], ['deal-parties']]) {
        queryClient.invalidateQueries({ queryKey: key })
      }
    },
    onError: (failure) => setNote({ tone: 'error', text: crmErrorDetail(failure, 'The merge could not be undone.') }),
  })

  const items = log.data?.items ?? []
  return (
    <div className="panel flex min-h-0 flex-col" aria-label="Merge log">
      <div className="panel-header">
        <span className="panel-header-title">Merge log</span>
        <span className="font-mono text-[9px] text-stone-500">{log.isSuccess ? items.length : '…'}</span>
      </div>
      <div className="panel-body min-h-0 flex-1 overflow-y-auto scrollbar-thin">
        {note ? (
          <div className={note.tone === 'error' ? 'mb-1.5 text-[10px] text-red-300' : 'mb-1.5 text-[10px] text-stone-400'} role="status">
            {note.text}
          </div>
        ) : null}
        {log.isPending ? (
          <div className="flex items-center gap-1.5 text-[10px] text-stone-500">
            <Loader2 size={10} className="motion-safe:animate-spin" /> Reading the log…
          </div>
        ) : log.isError ? (
          <div className="text-[10px] text-red-300">{crmErrorDetail(log.error, 'The merge log could not be read.')}</div>
        ) : items.length === 0 ? (
          <div className="text-[11px] italic text-stone-500">No merges yet — duplicates are folded here as they are found.</div>
        ) : (
          <ul className="space-y-1.5">
            {items.map((entry) => (
              <li key={entry.id} className="rounded border border-amber-400/10 bg-black/20 px-2 py-1.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 text-[11px] text-stone-200">
                    <NameLink id={entry.source.id} name={entry.source.name} onOpen={onOpenContact} />
                    <span className="text-stone-500"> into </span>
                    <NameLink id={entry.target.id} name={entry.target.name} onOpen={onOpenContact} />
                  </div>
                  {entry.undone ? (
                    <span className="shrink-0 font-mono text-[9px] uppercase tracking-wider text-stone-500">Undone</span>
                  ) : (
                    <button
                      type="button"
                      className={BUTTON}
                      disabled={!entry.undoable || undo.isPending}
                      title={entry.blockedReason ?? 'Put both records back as they were'}
                      aria-label={`Undo merge of ${entry.source.name} into ${entry.target.name}`}
                      onClick={() => undo.mutate(entry.id)}
                    >
                      <RotateCcw size={10} /> Undo
                    </button>
                  )}
                </div>
                <div className="mt-0.5 text-[10px] text-stone-400">{entry.reason}</div>
                <div className="mt-0.5 text-[9px] text-stone-500">
                  {entry.automatic ? 'Automatic' : 'By hand'} · {entry.actor ?? 'not recorded'} ·{' '}
                  <span title={formatStamp(entry.createdAt)}>{relativeTime(entry.createdAt)}</span>
                  {entry.undone ? ` · undone by ${entry.undoneBy ?? 'not recorded'}` : ''}
                  {!entry.undone && entry.blockedReason ? ` · ${entry.blockedReason}` : ''}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

function NameLink({ id, name, onOpen }: { id: string; name: string; onOpen?: (id: string) => void }) {
  if (!onOpen) return <span>{name}</span>
  return (
    <button
      type="button"
      onClick={() => onOpen(id)}
      className="text-left underline decoration-dotted underline-offset-2 hover:text-amber-200"
    >
      {name}
    </button>
  )
}
